<template>
  <div :class="[calendarClass, 'vdp-datepicker__calendar']" :style="calendarStyle" @mousedown.prevent>
    <header>
      <template v-if="!disabled">
        <i @click="fillAll" class="el-icon-menu fill-all-button" title="Fill all working days"></i>
        <i @click="clearAll" v-if="this.value.length > 0" class="el-icon-close clear-all-button"
          title="Clear all selected"></i>
      </template>
      <span @click="showMonthCalendar">{{ isYmd ? currYearName : currMonthName }}
        {{ isYmd ? currMonthName : currYearName }}</span>
    </header>
    <div :class="isRtl ? 'flex-rtl' : ''">
      <span class="cal-cell day-header" v-for="d in daysOfWeek" :key="d.timestamp">{{ d }}</span>
      <template v-if="blankDays > 0">
        <span class="cal-cell day blank" v-for="d in blankDays" :key="d.timestamp"></span>
      </template>
      <template v-for="day in days">
        <span class="cal-cell day" :key="day.timestamp" :class="dayClasses(day)" v-html="dayCellContent(day)"
          @mousedown="startDateDragging(day)" @mouseover="dragging && selectDateDrag(day)"></span>
      </template>
    </div>
  </div>
</template>

<script>
  import { makeDateUtils } from '@/utilities/DateUtils'
  import en from './locale/translations/en'
  import { differenceInCalendarDays } from 'date-fns'

  export default {
    props: {
      value: Array,
      disabled: Boolean,
      showDayView: Boolean,
      pageDate: {
        type: Date,
        default: () => new Date()
      },
      pageTimestamp: Number,
      fullMonthName: Boolean,
      dayCellContent: {
        type: Function,
        default: day => day.date
      },
      disabledDates: Object,
      highlighted: Object,
      calendarClass: [String, Object, Array],
      calendarStyle: Object,
      translation: {
        type: Object,
        default: () => en
      },
      isRtl: Boolean,
      mondayFirst: Boolean,
      useUtc: Boolean
    },
    data() {
      const constructedDateUtils = makeDateUtils(this.useUtc)
      return {
        utils: constructedDateUtils,
        dragging: false
      }
    },
    computed: {
      /**
       * Returns an array of day names
       * @return {String[]}
       */
      daysOfWeek() {
        if (this.mondayFirst) {
          const tempDays = this.translation.days.slice()
          tempDays.push(tempDays.shift())
          return tempDays
        }
        return this.translation.days
      },
      /**
       * Returns the day number of the week less one for the first of the current month
       * Used to show amount of empty cells before the first in the day calendar layout
       * @return {Number}
       */
      blankDays() {
        const d = this.pageDate
        let dObj = this.useUtc
          ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
          : new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
        if (this.mondayFirst) {
          return this.utils.getDay(dObj) > 0 ? this.utils.getDay(dObj) - 1 : 6
        }
        return this.utils.getDay(dObj)
      },
      /**
       * @return {Object[]}
       */
      days() {
        const d = this.pageDate
        let days = []
        // set up a new date object to the beginning of the current 'page'
        let dObj = this.useUtc
          ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
          : new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
        let daysInMonth = this.utils.daysInMonth(this.utils.getFullYear(dObj), this.utils.getMonth(dObj))
        let currentDate = new Date()

        for (let i = 0; i < daysInMonth; i++) {
          days.push({
            date: this.utils.getDate(dObj),
            timestamp: dObj.getTime(),
            isSelected: this.isSelectedDate(this.utils.getDate(dObj)),
            isDisabled: this.isDisabledDate(dObj) || differenceInCalendarDays(dObj, currentDate) <= 0,
            isHighlighted: this.isHighlightedDate(dObj),
            isHighlightStart: this.isHighlightStart(dObj),
            isHighlightEnd: this.isHighlightEnd(dObj),
            isToday: this.utils.compareDates(dObj, new Date()),
            isWeekend: this.utils.getDay(dObj) === 0 || this.utils.getDay(dObj) === 6,
            isSaturday: this.utils.getDay(dObj) === 6,
            isSunday: this.utils.getDay(dObj) === 0
          })
          this.utils.setDate(dObj, this.utils.getDate(dObj) + 1)
        }
        return days
      },
      /**
       * Gets the name of the month the current page is on
       * @return {String}
       */
      currMonthName() {
        const monthName = this.fullMonthName ? this.translation.months : this.translation.monthsAbbr
        return this.utils.getMonthNameAbbr(this.utils.getMonth(this.pageDate), monthName)
      },
      /**
       * Gets the name of the year that current page is on
       * @return {Number}
       */
      currYearName() {
        const yearSuffix = this.translation.yearSuffix
        return `${this.utils.getFullYear(this.pageDate)}${yearSuffix}`
      },
      /**
       * Is this translation using year/month/day format?
       * @return {Boolean}
       */
      isYmd() {
        return this.translation.ymd && this.translation.ymd === true
      }
    },
    methods: {
      fillAll() {
        let changed = false
        for (let i = 0; i < this.days.length; i++) {
          if (!this.days[i].isDisabled && !this.days[i].isWeekend && !this.value.find(x => x.date === this.days[i].date)) {
            this.value.push({ date: this.days[i].date, id: 0 })
            changed = true
          }
        }
        if (changed) {
          this.$emit('change', this.value)
        }
      },
      clearAll() {
        let changed = false
        // need to iterate in reverse so we can splice off the top
        for (let i = this.value.length - 1; i >= 0; i--) {
          if (this.days.some(y => y.date === this.value[i].date && !y.isDisabled)) {
            this.value.splice(i, 1)
            changed = true
          }
        }
        if (changed) {
          this.$emit('change', this.value)
        }
      },
      getSelectedClass(date) {
        let i = this.selectedDays.findIndex(x => x === date.date)
        if (i > -1) {
          return 'selected'
        }
      },
      startDateDragging(date) {
        if (this.disabled || date.isDisabled) {
          return
        }
        this.dragging = true
        window.addEventListener('mouseup', () => {
          this.dragging = false
        })
        this.selectDate(date)
      },
      selectDateDrag(date) {
        if (!this.value.some(x => x.date === date.date) && !date.isDisabled) {
          this.value.push({ date: date.date, id: 0 })
        }
        this.$emit('change', this.value)
      },
      selectDate(date) {
        let i = this.value.findIndex(x => x.date === date.date)
        if (i > -1) {
          this.value.splice(i, 1)
        } else {
          this.value.push({ date: date.date, id: 0 })
        }

        this.$emit('change', this.value)
      },
      /**
       * @return {Number}
       */
      getPageMonth() {
        return this.utils.getMonth(this.pageDate)
      },
      /**
       * Emit an event to show the month picker
       */
      showMonthCalendar() {
        this.$emit('showMonthCalendar')
      },
      /**
       * Whether a day is selected
       * @param {Date}
       * @return {Boolean}
       */
      isSelectedDate(date) {
        let selected = this.value.some(x => x.date === date)
        if (selected) {
          return true
        }
        return false
        // return this.selectedDate && this.utils.compareDates(this.selectedDate, dObj)
      },
      /**
       * Whether a day is disabled
       * @param {Date}
       * @return {Boolean}
       */
      isDisabledDate(date) {
        let disabledDates = false

        if (typeof this.disabledDates === 'undefined') {
          return false
        }

        if (typeof this.disabledDates.dates !== 'undefined') {
          this.disabledDates.dates.forEach((d) => {
            if (this.utils.compareDates(date, d)) {
              disabledDates = true
              return true
            }
          })
        }
        if (typeof this.disabledDates.to !== 'undefined' && this.disabledDates.to && date < this.disabledDates.to) {
          disabledDates = true
        }
        if (typeof this.disabledDates.from !== 'undefined' && this.disabledDates.from && date > this.disabledDates.from) {
          disabledDates = true
        }
        if (typeof this.disabledDates.ranges !== 'undefined') {
          this.disabledDates.ranges.forEach((range) => {
            if (typeof range.from !== 'undefined' && range.from && typeof range.to !== 'undefined' && range.to) {
              if (date < range.to && date > range.from) {
                disabledDates = true
                return true
              }
            }
          })
        }
        if (typeof this.disabledDates.days !== 'undefined' && this.disabledDates.days.indexOf(this.utils.getDay(date)) !== -1) {
          disabledDates = true
        }
        if (typeof this.disabledDates.daysOfMonth !== 'undefined' && this.disabledDates.daysOfMonth.indexOf(this.utils.getDate(date)) !== -1) {
          disabledDates = true
        }
        if (typeof this.disabledDates.customPredictor === 'function' && this.disabledDates.customPredictor(date)) {
          disabledDates = true
        }
        return disabledDates
      },
      /**
       * Whether a day is highlighted (only if it is not disabled already except when highlighted.includeDisabled is true)
       * @param {Date}
       * @return {Boolean}
       */
      isHighlightedDate(date) {
        if (!(this.highlighted && this.highlighted.includeDisabled) && this.isDisabledDate(date)) {
          return false
        }

        let highlighted = false

        if (typeof this.highlighted === 'undefined') {
          return false
        }

        if (typeof this.highlighted.dates !== 'undefined') {
          this.highlighted.dates.forEach((d) => {
            if (this.utils.compareDates(date, d)) {
              highlighted = true
              return true
            }
          })
        }

        if (this.isDefined(this.highlighted.from) && this.isDefined(this.highlighted.to)) {
          highlighted = date >= this.highlighted.from && date <= this.highlighted.to
        }

        if (typeof this.highlighted.days !== 'undefined' && this.highlighted.days.indexOf(this.utils.getDay(date)) !== -1) {
          highlighted = true
        }

        if (typeof this.highlighted.daysOfMonth !== 'undefined' && this.highlighted.daysOfMonth.indexOf(this.utils.getDate(date)) !== -1) {
          highlighted = true
        }

        if (typeof this.highlighted.customPredictor === 'function' && this.highlighted.customPredictor(date)) {
          highlighted = true
        }

        return highlighted
      },
      dayClasses(day) {
        return {
          'selected': day.isSelected,
          'disabled': day.isDisabled,
          'highlighted': day.isHighlighted,
          'today': day.isToday,
          'weekend': day.isWeekend && !day.isSelected,
          'sat': day.isSaturday,
          'sun': day.isSunday,
          'highlight-start': day.isHighlightStart,
          'highlight-end': day.isHighlightEnd
        }
      },
      /**
       * Whether a day is highlighted and it is the first date
       * in the highlighted range of dates
       * @param {Date}
       * @return {Boolean}
       */
      isHighlightStart(date) {
        return this.isHighlightedDate(date) &&
          (this.highlighted.from instanceof Date) &&
          (this.utils.getFullYear(this.highlighted.from) === this.utils.getFullYear(date)) &&
          (this.utils.getMonth(this.highlighted.from) === this.utils.getMonth(date)) &&
          (this.utils.getDate(this.highlighted.from) === this.utils.getDate(date))
      },
      /**
       * Whether a day is highlighted and it is the first date
       * in the highlighted range of dates
       * @param {Date}
       * @return {Boolean}
       */
      isHighlightEnd(date) {
        return this.isHighlightedDate(date) &&
          (this.highlighted.to instanceof Date) &&
          (this.utils.getFullYear(this.highlighted.to) === this.utils.getFullYear(date)) &&
          (this.utils.getMonth(this.highlighted.to) === this.utils.getMonth(date)) &&
          (this.utils.getDate(this.highlighted.to) === this.utils.getDate(date))
      },
      /**
       * Helper
       * @param  {mixed}  prop
       * @return {Boolean}
       */
      isDefined(prop) {
        return typeof prop !== 'undefined' && prop
      }
    }
  }
</script>

<style lang="stylus">
  @import './styles'
</style>
